import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { govPageContainer, GOV_TYPE } from '../ui/PatientUIGovernor';
import { patientApi } from '../api';
import type { LabReport } from '../types';
import LabResultTable, { resultsAsText } from '../components/LabResultTable';

/**
 * One row on this page: a lab booking, or a released report, or a booking that
 * has become one.
 *
 * It used to be the device store's `LabOrder`, which is why the store was
 * merged in at all. Nothing here is stored on the device any more — the vault
 * of record is the server — so the row is this page's own shape.
 */
type Row = {
  id: string;
  testName: string;
  labName: string;
  date: string;
  time: string;
  collectionType: string;
  status: 'BOOKED' | 'SAMPLE_COLLECTED' | 'PROCESSING' | 'COMPLETED' | 'CANCELLED';
  createdAt: string;
  reportNumber?: string;
  /** The released report behind this row, when there is one to open. */
  reportId?: string;
  /** The LAB's own status word, alongside the five this page draws. */
  labStatus?: string;
  isCritical?: boolean;
  criticalCount?: number;
};

/* ── SVG Icons (inline) ───────────────────────────────────── */

const IconFlask = ({ size = 18, color = T.teal }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 3h6M10 3v6.5L4.5 19a1.5 1.5 0 001.3 2.2h12.4a1.5 1.5 0 001.3-2.2L14 9.5V3" />
  </svg>
);

const IconCalendar = ({ size = 14, color = T.textTer }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const IconClock = ({ size = 14, color = T.textTer }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
  </svg>
);

const IconPlus = ({ size = 16, color = T.white }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const IconX = ({ size = 14, color = T.danger }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const IconHome = ({ size = 13, color = T.teal }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z" /><polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const IconWalk = ({ size = 13, color = T.navy }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="5" r="2" /><path d="M10 22l2-7 3 3v6" /><path d="M14 13l-3-3-2 4" />
  </svg>
);

const IconEmpty = ({ size = 56, color = T.border }: { size?: number; color?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 3h6M10 3v6.5L4.5 19a1.5 1.5 0 001.3 2.2h12.4a1.5 1.5 0 001.3-2.2L14 9.5V3" />
    <line x1="7" y1="15" x2="17" y2="15" strokeDasharray="2 2" />
  </svg>
);

/* ── Helpers ───────────────────────────────────────────────── */

type FilterTab = 'ALL' | 'BOOKED' | 'COMPLETED' | 'CANCELLED';

const STATUS_COLOR: Record<string, string> = {
  BOOKED: T.info,
  SAMPLE_COLLECTED: T.warning,
  PROCESSING: T.warning,
  COMPLETED: T.success,
  CANCELLED: T.danger,
};

const STATUS_LABEL: Record<string, string> = {
  BOOKED: 'Booked',
  SAMPLE_COLLECTED: 'Sample Collected',
  PROCESSING: 'Processing',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
};

function fmtDate(d: string): string {
  try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return d; }
}

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

/* ── Component ────────────────────────────────────────────── */

export default function PatientLabReports() {
  const navigate = useNavigate();

  const [apiOrders, setApiOrders] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<FilterTab>('ALL');
  /** The open report's findings, keyed by report id, fetched on demand. */
  const [openReport, setOpenReport] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, LabReport>>({});
  const [resultsError, setResultsError] = useState<string | null>(null);
  const [loadingResults, setLoadingResults] = useState(false);

  /* Backend lab-booking statuses → this page's coarser tabs. */
  const mapBookingStatus = (s: string): Row['status'] => {
    if (s === 'SAMPLE_COLLECTED') return 'SAMPLE_COLLECTED';
    if (s === 'IN_PROGRESS') return 'PROCESSING';
    if (s === 'COMPLETED' || s === 'REPORT_READY') return 'COMPLETED';
    if (s === 'CANCELLED') return 'CANCELLED';
    return 'BOOKED'; // REQUESTED / ACCEPTED / SCHEDULED
  };

  /**
   * Open a report and fetch what the lab measured.
   *
   * The list payload deliberately carries no results; `GET /lab/reports/:id`
   * is the only place they exist, and nothing in this app asked for them — so
   * a patient could be told a result was critical and never see which.
   */
  const toggleResults = async (reportId: string) => {
    if (openReport === reportId) { setOpenReport(null); return; }
    setOpenReport(reportId);
    setResultsError(null);
    if (results[reportId]) return;
    setLoadingResults(true);
    try {
      const full = await patientApi.getLabReport(reportId);
      setResults(prev => ({ ...prev, [reportId]: full }));
    } catch {
      setResultsError('The lab\u2019s findings could not be loaded. Try again in a moment.');
    } finally {
      setLoadingResults(false);
    }
  };

  /**
   * Two real feeds, folded into one list.
   *
   * `/lab/bookings` is what the patient asked for; `/lab/reports` is what the
   * lab released, and a released report is joined onto its own booking by
   * `patientBookingId`. They are fetched independently so a failure in one
   * cannot hide the other.
   *
   * The comment here used to say the reports feed "has no backend model yet"
   * and returned "an honest empty list until the lab-reports subsystem
   * exists". Both stopped being true a long time ago.
   */
  useEffect(() => {
    let alive = true;
    setError(null);

    const load = async () => {
      const bookings = await patientApi.lab.getOrders().catch(() => null);
      if (alive) {
        if (bookings) {
          const raw: any[] = Array.isArray(bookings) ? bookings : (bookings as any)?.data ?? [];
          setApiOrders(raw.map((b: any): Row => ({
            id: b.id || b._id || '',
            testName: b.testName || b.tests?.[0] || 'Lab Test',
            labName: b.labName || b.providerName || 'Lab',
            date: b.date || b.scheduledAt || b.createdAt || '',
            time: b.scheduledAt
              ? new Date(b.scheduledAt).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })
              : '',
            collectionType: b.collectionType === 'HOME_COLLECTION' ? 'Home Collection' : 'Walk-in',
            status: mapBookingStatus(b.status || 'REQUESTED'),
            createdAt: b.createdAt || '',
          })));
        } else {
          setError('Could not load your lab orders. Please refresh the page.');
        }
      }

      /**
       * Released reports, through the mapper that reads what the endpoint
       * actually sends.
       *
       * This block used to map the payload by hand and read nine keys the
       * server has never sent — `testName`, `tests[0].testName`,
       * `order.labProfile.labName`, `reportDate`, `time`, `collectionType`,
       * `reportUrl`, `pdfUrl`. Every report therefore fell through to its own
       * report NUMBER for a title, showed no date, and hardcoded COMPLETED
       * over the lab's real status. `hasCriticalResult` was never read at all.
       */
      const reports = await patientApi.getLabReports().catch(() => null);
      if (alive && reports && reports.length > 0) {
        setApiOrders(prev => {
          const next = [...prev];
          for (const r of reports) {
            const row: Row = {
              id: r.id,
              testName: r.testName || r.reportNumber || 'Lab test',
              labName: r.labName || 'Lab',
              date: r.reportDate,
              time: '',
              collectionType: '',
              status: 'COMPLETED',
              createdAt: r.reportDate,
              reportNumber: r.reportNumber,
              isCritical: r.isCritical,
              criticalCount: r.criticalCount,
              labStatus: r.status,
            };
            /**
             * A released report and the booking it came from are different id
             * spaces, so this used to append the report as a SECOND row beside
             * its own booking — the same tests, the same lab, listed twice.
             * The server carries `patientBookingId` for exactly this.
             */
            const at = r.patientBookingId
              ? next.findIndex(o => o.id === r.patientBookingId)
              : -1;
            if (at >= 0) next[at] = { ...next[at]!, ...row, id: next[at]!.id, reportId: r.id };
            else if (!next.some(o => o.id === r.id)) next.push({ ...row, reportId: r.id });
          }
          return next;
        });
      }

      if (alive) setLoading(false);
    };
    load();
    return () => { alive = false; };
  }, []);

  /**
   * What the server says, newest first.
   *
   * The device's own `labOrders` store used to be merged in on top of this.
   * Those were copies: they did not survive a reinstall, never reached another
   * phone, and their status only ever moved when this app moved it — so a
   * booking the lab had already completed still read "Booked" here.
   */
  const allOrders = useMemo(
    () => [...apiOrders].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [apiOrders],
  );

  /* Filter */
  const filtered = useMemo(() => {
    if (activeTab === 'ALL') return allOrders;
    return allOrders.filter(o => o.status === activeTab);
  }, [allOrders, activeTab]);

  const tabs: { key: FilterTab; label: string }[] = [
    { key: 'ALL', label: 'All' },
    { key: 'BOOKED', label: 'Booked' },
    { key: 'COMPLETED', label: 'Completed' },
    { key: 'CANCELLED', label: 'Cancelled' },
  ];

  const [cancellingId, setCancellingId] = useState<string | null>(null);

  const handleCancel = async (id: string) => {
    // Every row on this page is the server's, so there is no device-only
    // order to mark cancelled locally any more — a cancellation that did not
    // reach the lab is not a cancellation.
    setCancellingId(id);
    try {
      await patientApi.lab.cancelAppointment(id);
      setApiOrders(prev => prev.map(o => (o.id === id ? { ...o, status: 'CANCELLED' } : o)));
    } catch {
      setError('Could not cancel this order. Please try again.');
    } finally {
      setCancellingId(null);
    }
  };

  /* ── Styles ────────────────────────────────────────────── */

  const S = {
    page: govPageContainer() as React.CSSProperties,
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 20,
      paddingTop: 4,
    } as React.CSSProperties,
    title: GOV_TYPE.pageTitle as React.CSSProperties,
    subtitle: {
      fontSize: 11,
      color: T.textTer,
      marginTop: 2,
    } as React.CSSProperties,
    bookBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      background: T.teal,
      color: T.white,
      border: 'none',
      borderRadius: 10,
      padding: '8px 14px',
      fontSize: 11,
      fontWeight: 600,
      fontFamily: T.font,
      cursor: 'pointer',
    } as React.CSSProperties,
    tabBar: {
      display: 'flex',
      gap: 0,
      background: T.ice,
      borderRadius: 10,
      padding: 3,
      marginBottom: 16,
    } as React.CSSProperties,
    tab: (active: boolean) => ({
      flex: 1,
      padding: '7px 0',
      fontSize: 11,
      fontWeight: active ? 700 : 500,
      color: active ? T.white : T.textSec,
      background: active ? T.teal : 'transparent',
      border: 'none',
      borderRadius: 8,
      cursor: 'pointer',
      fontFamily: T.font,
      transition: 'all .2s',
    } as React.CSSProperties),
    card: {
      background: T.white,
      borderRadius: 16,
      border: `1px solid ${T.border}`,
      padding: 14,
      marginBottom: 10,
      boxShadow: T.shadow,
    } as React.CSSProperties,
    cardTop: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: 10,
    } as React.CSSProperties,
    iconCircle: {
      width: 38,
      height: 38,
      borderRadius: 12,
      background: hexToRgba(T.teal, 0.08),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    } as React.CSSProperties,
    cardBody: {
      flex: 1,
      minWidth: 0,
    } as React.CSSProperties,
    testName: {
      fontSize: 13,
      fontWeight: 700,
      color: T.text,
      margin: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const,
    } as React.CSSProperties,
    labName: {
      fontSize: 11,
      color: T.textSec,
      marginTop: 2,
    } as React.CSSProperties,
    meta: {
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      marginTop: 8,
      flexWrap: 'wrap' as const,
    } as React.CSSProperties,
    metaItem: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      fontSize: 9,
      color: T.textTer,
    } as React.CSSProperties,
    badge: (bgColor: string) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      fontSize: 9,
      fontWeight: 700,
      color: bgColor,
      background: hexToRgba(bgColor, 0.1),
      borderRadius: 6,
      padding: '3px 7px',
      letterSpacing: 0.3,
    } as React.CSSProperties),
    statusDot: (color: string) => ({
      width: 5,
      height: 5,
      borderRadius: '50%',
      background: color,
      flexShrink: 0,
    } as React.CSSProperties),
    cardActions: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      marginTop: 10,
      paddingTop: 8,
      borderTop: `1px solid ${T.border}`,
    } as React.CSSProperties,
    cancelBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 4,
      background: hexToRgba(T.danger, 0.08),
      color: T.danger,
      border: `1px solid ${hexToRgba(T.danger, 0.2)}`,
      borderRadius: 8,
      padding: '5px 10px',
      fontSize: 9,
      fontWeight: 600,
      cursor: 'pointer',
      fontFamily: T.font,
    } as React.CSSProperties,
    empty: {
      textAlign: 'center' as const,
      padding: '48px 20px',
    } as React.CSSProperties,
    emptyTitle: {
      fontSize: 13,
      fontWeight: 600,
      color: T.textSec,
      marginTop: 14,
    } as React.CSSProperties,
    emptyDesc: {
      fontSize: 11,
      color: T.textTer,
      marginTop: 4,
    } as React.CSSProperties,
    emptyBtn: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      background: T.teal,
      color: T.white,
      border: 'none',
      borderRadius: 10,
      padding: '10px 18px',
      fontSize: 11,
      fontWeight: 600,
      fontFamily: T.font,
      cursor: 'pointer',
      marginTop: 18,
    } as React.CSSProperties,
    skeleton: {
      background: T.ice,
      borderRadius: 16,
      padding: 18,
      marginBottom: 10,
    } as React.CSSProperties,
    skelLine: (w: string, h: number) => ({
      width: w,
      height: h,
      borderRadius: 6,
      background: T.border,
      marginBottom: 8,
    } as React.CSSProperties),
  };

  /* ── Render ────────────────────────────────────────────── */

  if (loading) {
    return (
      <div style={S.page}>
        <div style={S.header}>
          <div>
            <h1 style={S.title}>Lab Reports</h1>
            <p style={S.subtitle}>Your diagnostic orders</p>
          </div>
        </div>
        {[1, 2, 3].map(i => (
          <div key={i} style={S.skeleton}>
            <div style={S.skelLine('55%', 12)} />
            <div style={S.skelLine('35%', 10)} />
            <div style={S.skelLine('25%', 8)} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <h1 style={S.title}>Lab Reports</h1>
          <p style={S.subtitle}>Your diagnostic orders</p>
        </div>
        <button style={S.bookBtn} onClick={() => navigate('/patient/book-lab')}>
          <IconPlus size={14} /> Book Lab Test
        </button>
      </div>

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 10, padding: '8px 12px', marginBottom: 12 }}>
          {error}
        </div>
      )}

      {/* Filter Tabs */}
      <div style={S.tabBar}>
        {tabs.map(t => (
          <button key={t.key} style={S.tab(activeTab === t.key)} onClick={() => setActiveTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div style={S.empty}>
          <IconEmpty />
          <p style={S.emptyTitle}>
            {activeTab === 'ALL' ? 'No lab orders yet' : `No ${activeTab.toLowerCase()} orders`}
          </p>
          <p style={S.emptyDesc}>Book a lab test to get started</p>
          {activeTab === 'ALL' && (
            <button style={S.emptyBtn} onClick={() => navigate('/patient/book-lab')}>
              <IconPlus size={14} /> Book Lab Test
            </button>
          )}
        </div>
      ) : (
        <div>
        {filtered.map((order) => {
          const statusColor = STATUS_COLOR[order.status] || T.textTer;
          const isHome = order.collectionType === 'Home Collection';
          return (
            <div key={order.id} style={S.card}>
              <div style={S.cardTop}>
                <div style={S.iconCircle}>
                  <IconFlask size={18} color={T.teal} />
                </div>
                <div style={S.cardBody}>
                  <p style={S.testName}>{order.testName}</p>
                  <p style={S.labName}>{order.labName}</p>
                  {order.reportNumber && (
                    <div style={{ fontSize: 9, color: T.teal, fontFamily: 'monospace', fontWeight: 600, marginTop: 2 }}>
                      {order.reportNumber}
                    </div>
                  )}
                  <div style={S.meta}>
                    <span style={S.metaItem}>
                      <IconCalendar size={11} color={T.textTer} />
                      {fmtDate(order.date)}
                    </span>
                    {order.time && (
                      <span style={S.metaItem}>
                        <IconClock size={11} color={T.textTer} />
                        {order.time}
                      </span>
                    )}
                    <span style={S.badge(isHome ? T.teal : T.navy)}>
                      {isHome ? <IconHome size={10} color={T.teal} /> : <IconWalk size={10} color={T.navy} />}
                      {order.collectionType}
                    </span>
                    <span style={S.badge(statusColor)}>
                      <span style={S.statusDot(statusColor)} />
                      {STATUS_LABEL[order.status] || order.status}
                    </span>
                  </div>
                  {/* Only claim NeoVerified for orders confirmed by the real lab
                      API (carrying a backend-issued report number) — never for
                      locally-created orders marked complete on-device. */}
                  {order.status === 'COMPLETED' && order.reportNumber && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6, fontSize: 9, fontWeight: 700, color: '#0A7B4F', background: 'rgba(10,123,79,0.08)', padding: '2px 8px', borderRadius: 6 }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#0A7B4F" strokeWidth="2.5"><path d="M9 12l2 2 4-4" /><circle cx="12" cy="12" r="10" /></svg>
                      NeoVerified · {order.reportNumber}
                    </div>
                  )}
                  {/* The LAB flagged a finding. This exists precisely so a list
                      card can warn without the patient having to open the
                      report; the page never read it, so a critical result
                      looked like every other row. */}
                  {order.isCritical && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 6, fontSize: 9.5, fontWeight: 800, color: '#B91C1C', background: '#FEF2F2', border: '1px solid #FECACA', padding: '3px 9px', borderRadius: 6 }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#B91C1C" strokeWidth="2.5" strokeLinecap="round">
                        <path d="M12 8v5" /><circle cx="12" cy="17" r="0.5" fill="#B91C1C" /><circle cx="12" cy="12" r="10" />
                      </svg>
                      {order.criticalCount === 1
                        ? 'The lab flagged 1 result as critical'
                        : `The lab flagged ${order.criticalCount} results as critical`}
                    </div>
                  )}
                </div>
              </div>

              {/* Only a released report has findings to open. A booking that
                  the lab has not reported on has nothing behind it yet. */}
              {order.reportId && (
                <>
                  <div style={{ ...S.cardActions, justifyContent: 'flex-start', gap: 8 }}>
                    <button
                      onClick={() => toggleResults(order.reportId!)}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        background: hexToRgba(T.teal, 0.08), color: T.teal,
                        border: `1px solid ${hexToRgba(T.teal, 0.2)}`,
                        borderRadius: 8, padding: '5px 10px',
                        fontSize: 9, fontWeight: 600, cursor: 'pointer', fontFamily: T.font,
                      }}
                    >
                      {openReport === order.reportId ? 'Hide results' : 'View results'}
                    </button>
                    {/* Shares what the lab measured, not four lines about it.
                        This used to be window.print(), which printed the
                        browser page, and a share with no results in it. */}
                    <button
                      onClick={() => {
                        const full = results[order.reportId!];
                        const text = resultsAsText({
                          testName: order.testName,
                          labName: order.labName,
                          reportNumber: order.reportNumber,
                          reportDate: fmtDate(order.date),
                          results: full?.results,
                        });
                        if (navigator.share) navigator.share({ title: order.testName, text }).catch(() => {});
                        else navigator.clipboard.writeText(text).catch(() => {});
                      }}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                        background: hexToRgba(T.navy, 0.08), color: T.navy,
                        border: `1px solid ${hexToRgba(T.navy, 0.2)}`,
                        borderRadius: 8, padding: '5px 10px',
                        fontSize: 9, fontWeight: 600, cursor: 'pointer', fontFamily: T.font,
                      }}
                    >
                      Share report
                    </button>
                  </div>

                  {openReport === order.reportId && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
                      {(() => {
                        if (loadingResults && !results[order.reportId!]) {
                          return <p style={{ fontSize: 11, color: T.textSec, fontFamily: T.font }}>Loading the lab&rsquo;s findings…</p>;
                        }
                        if (resultsError) {
                          return <p style={{ fontSize: 11, color: T.danger, fontFamily: T.font }}>{resultsError}</p>;
                        }
                        return <LabResultTable results={results[order.reportId!]?.results} />;
                      })()}
                    </div>
                  )}
                </>
              )}

              {/* Payment Status Badge */}
              {(order as any).paymentMode && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${T.border}` }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.textTer} strokeWidth="2"><path d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" /></svg>
                  <span style={{
                    fontSize: 9, fontWeight: 700, fontFamily: T.font, borderRadius: 6, padding: '2px 8px',
                    ...((order as any).paymentMode === 'ONLINE' || (order as any).paymentMode === 'PAID'
                      ? { color: '#0A7B4F', background: 'rgba(10,123,79,0.08)' }
                      : { color: '#7C3AED', background: 'rgba(124,58,237,0.08)' }),
                  }}>
                    {(order as any).paymentMode === 'ONLINE' || (order as any).paymentMode === 'PAID' ? 'Paid' : 'Pay at Lab'}
                  </span>
                </div>
              )}

              {order.status === 'BOOKED' && (
                <div style={S.cardActions}>
                  <button
                    style={{ ...S.cancelBtn, opacity: cancellingId === order.id ? 0.6 : 1 }}
                    disabled={cancellingId === order.id}
                    onClick={() => handleCancel(order.id)}
                  >
                    <IconX size={10} color={T.danger} /> {cancellingId === order.id ? 'Cancelling…' : 'Cancel Order'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
        <div style={{ textAlign: 'center', padding: '16px 0 4px' }}>
          <span style={{ fontFamily: T.font, fontSize: 9, color: T.textTer, lineHeight: 1.6 }}>
            NeoHealthCard™ · Lab orders synced with your NeoHealthCard account · Every access is audit logged
          </span>
        </div>
        </div>
      )}
    </div>
  );
}
